import { useRef } from "react";

import { calculateDisplayUnit } from "../components/filter/util/calculateDisplayUnit";
import { useEvaUnitCore } from "./useEvaUnitCore";
import useUserUnit from "./useUserUnit";

interface UseNormalizeFieldConversionParams {
  field: string;
  fallbackDisplay: string;
  fallbackUnit: string;
  units?: { [key: string]: string };
  defaultUnit?: string;
}

export default function useNormalizeFieldConversion({
  field,
  fallbackDisplay,
  fallbackUnit,
  units,
  defaultUnit
}: UseNormalizeFieldConversionParams): { displayLabel: string; displayUnit: string } {
  const unitType = useUserUnit();
  const { isReady, getTargetUnit } = useEvaUnitCore();

  // Use ref to store getTargetUnit to avoid unnecessary re-renders
  const getTargetUnitRef = useRef(getTargetUnit);
  getTargetUnitRef.current = getTargetUnit;

  // Case 1: Has units dictionary - use it directly
  if (units && units[unitType]) {
    return {
      displayLabel: fallbackDisplay,
      displayUnit: units[unitType]
    };
  }

  // Case 2: Has defaultUnit but no units mapping - use eva-unit-core to convert
  // This handles custom fields (org-defined/user-defined columns)
  if (isReady && defaultUnit) {
    const convertedUnit = getTargetUnitRef.current(defaultUnit, unitType);
    if (convertedUnit) {
      return {
        displayLabel: fallbackDisplay,
        displayUnit: convertedUnit
      };
    }
  }

  // Fallback to calculateDisplayUnit for other cases
  const displayUnit = calculateDisplayUnit(fallbackUnit, field, fallbackDisplay);
  return {
    displayLabel: fallbackDisplay,
    displayUnit
  };
}
